import React, { useState, useEffect, useRef, useCallback } from "react";
import classes from "./style.module.scss";
import { useAppHeader } from "~/app/modules/appHeader";
import usePageTitle from "~/app/hooks/usePageTitle";
import { useHistory, useLocation } from "react-router-dom";
import { route } from "~/app/helpers/route";
import { TIME_MANAGEMENT_ROUTES } from '~/timeManagement/Router';
import { useStoreActions, useStoreState } from "~/store";
import useTranslation from '~/app/hooks/useTranslation';


interface ActiveTimeProps {
  timeManagementId?: number;
  project?: any;
  mode?: string;
  activity?: any;
  machine?: any;
  isPaused?: boolean;
  returnFromSummary?: boolean;
  fromList?: boolean;
  newEntryData?: {
    projectId: number;
    machineId: number | null;
    activityId: number | null;
    modeType: number;
    notes?: string;
  };
}

/** API returns minutes; store fallback is hours. */
const resolveStandardDayMinutes = (
  apiValue?: number | null,
  fallbackHours = 7.5
): number => {
  if (apiValue != null && apiValue > 0) {
    return apiValue >= 60 ? apiValue : Math.round(apiValue * 60);
  }
  return Math.round(fallbackHours * 60);
};

const ActiveTime: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { setData, clearData } = useAppHeader();

  const getActiveEntry = useStoreActions((actions: any) => actions.timeManagement.getActiveEntry);
  const pauseResumeTimer = useStoreActions((actions: any) => actions.timeManagement.pauseResumeTimer);
  const addTimeManagement = useStoreActions((actions: any) => actions.timeManagement.save);
  const getActivities = useStoreActions((actions: any) => actions.timeManagement.getAllActivitiesData);
  const standardWorkingHoursPerDay = useStoreState((state) => state.machines.standardWorkingHoursPerDay);

  const locationState = (location.state || {}) as ActiveTimeProps;

  const [isPaused, setIsPaused] = useState(false);
  const [totalMinutesAllEntries, setTotalMinutesAllEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isStopping, setIsStopping] = useState(false);
  const [isPauseResuming, setIsPauseResuming] = useState(false);
  const [entryData, setEntryData] = useState<any>(null);
  const [allEntries, setAllEntries] = useState<any[]>([]);
  const [showResumeUI, setShowResumeUI] = useState(false);
  const [showLunchDialog, setShowLunchDialog] = useState(false);
  const [showOvertimeDialog, setShowOvertimeDialog] = useState(false);
  const [isOvertimeMode, setIsOvertimeMode] = useState(false);
  const [apiStandardHours, setApiStandardHours] = useState<number | undefined>(undefined);

  // Activity modal states
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<any>(null);

  // Unspecified modal states
  const [showUnspecifiedModal, setShowUnspecifiedModal] = useState(false);
  const [unspecifiedDescription, setUnspecifiedDescription] = useState('');

  const timeManagementIdRef = useRef<number>(0);
  const allEntriesRef = useRef<any[]>([]);
  const hasShownOvertimeDialogRef = useRef(false);
  const isInitializedRef = useRef(false);
  const lastRolloverDateRef = useRef<string>('');
  const rolloverInProgressRef = useRef(false);
  const [hasRolledOverDate, setHasRolledOverDate] = useState(false);
  const hasProcessedNavigationRef = useRef(false);
  const isMountedRef = useRef(true);
  usePageTitle(t('timeManagement.activeTime'));

  const getOvertimePromptKey = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    return `overtimePrompt_${today}_${timeManagementIdRef.current}`;
  }, []);

  const hasShownOvertimePrompt = useCallback(() => {
    const key = getOvertimePromptKey();
    return sessionStorage.getItem(key) === 'true';
  }, [getOvertimePromptKey]);

  const setOvertimePromptShown = useCallback(() => {
    const key = getOvertimePromptKey();
    sessionStorage.setItem(key, 'true');
  }, [getOvertimePromptKey]);

  const getLunchPromptKey = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    return `lunchPrompt_${today}_${timeManagementIdRef.current}`;
  }, []);

  const hasShownLunchPrompt = () => {
    const key = getLunchPromptKey();
    return sessionStorage.getItem(key) === 'true';
  };

  useEffect(() => {
    setData({
      title: t('timeManagement.activeTime'),
      subTitle: '',
      backLink: undefined,
      isTimeManagementPage: false,
      showUser: false,
      machineId: '',
      projectId: '',
      parentFolderId: 0,
      showMachineMessages: false,
      isIssueDetailPage: false,
      notification: false,
      isAddTimeForm: false,
      isGridView: true
    });

    return () => clearData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setData, clearData]);

  useEffect(() => {
    if (!isInitializedRef.current) {
      hasShownOvertimeDialogRef.current = hasShownOvertimePrompt();
      isInitializedRef.current = true;
    }
  }, [hasShownOvertimePrompt]);

  useEffect(() => {
    if (locationState?.returnFromSummary === true && locationState?.timeManagementId) {
      timeManagementIdRef.current = locationState.timeManagementId;
      setShowResumeUI(true);
      setIsPaused(true);
    } else {
      setShowResumeUI(false);
    }
  }, [locationState?.returnFromSummary, locationState?.timeManagementId]);

  const fetchActiveEntry = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) {
        setIsLoading(true);
      }
      const response = await getActiveEntry();
      if (!isMountedRef.current) return;

      if (response?.data) {
        const entries = Array.isArray(response.data) ? response.data : (response.data.entries || []);

        if (!Array.isArray(entries) || entries.length === 0) {
          if (!silent) {
            history.push(route(TIME_MANAGEMENT_ROUTES.list));
          }
          return;
        }
        if (silent) {
          await new Promise(resolve => setTimeout(resolve, 1));
          if (!isMountedRef.current) return;
        }

        setAllEntries(entries);
        allEntriesRef.current = entries;

        const apiTotalMinutes = response.total_minutes_all || response.data.total_minutes_all || response.data.total_minutes || 0;
        setTotalMinutesAllEntries(apiTotalMinutes);

        if (response.standardWorkingHoursPerDay) {
          setApiStandardHours(resolveStandardDayMinutes(response.standardWorkingHoursPerDay));
        }

        const activeEntry = entries.find((entry: any) => entry.status === 2);
        const currentEntry = activeEntry || [...entries].sort((a, b) => b.id - a.id)[0];

        const standardDayMinutes = resolveStandardDayMinutes(
          response?.standardWorkingHoursPerDay,
          standardWorkingHoursPerDay
        );

        if (apiTotalMinutes < 240) {
          const key = getLunchPromptKey();
          sessionStorage.removeItem(key);
        }

        if (
          apiTotalMinutes >= standardDayMinutes &&
          !isOvertimeMode &&
          !hasShownOvertimeDialogRef.current
        ) {
          setShowOvertimeDialog(true);
          hasShownOvertimeDialogRef.current = true;
          setOvertimePromptShown();
        }

        if (apiTotalMinutes < standardDayMinutes) {
          const key = getOvertimePromptKey();
          sessionStorage.removeItem(key);
          hasShownOvertimeDialogRef.current = false;
        }

        timeManagementIdRef.current = currentEntry.id;
        setEntryData(currentEntry);

        const isCurrentlyPaused = currentEntry.status === 0 || currentEntry.is_paused === true;
        setIsPaused(isCurrentlyPaused);

      } else {
        if (!silent) {
          history.push(route(TIME_MANAGEMENT_ROUTES.list));
        }
      }
    } catch (error) {
      if (!silent) {
        history.push(route(TIME_MANAGEMENT_ROUTES.list));
      }
    } finally {
      if (!silent && isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    getActiveEntry,
    history,
    isOvertimeMode,
    getLunchPromptKey,
    getOvertimePromptKey,
    setOvertimePromptShown,
    standardWorkingHoursPerDay
  ]);
  useEffect(() => {
    fetchActiveEntry();
  }, [fetchActiveEntry]);

  const rolloverToNewDate = useCallback(async (currentEntry: any) => {
    if (rolloverInProgressRef.current) {
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    if (lastRolloverDateRef.current === today) {
      return;
    }

    try {
      rolloverInProgressRef.current = true;

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowDate = tomorrow.toISOString().split('T')[0];

      const allCurrentEntries = allEntriesRef.current;

      const tasksToUpdate = allCurrentEntries.map((entry: any) => {
        const taskData: any = {
          id: entry.id,
          date: tomorrowDate,
          update_date_only: true
        };

        return taskData;
      });

      await addTimeManagement({ tasks: tasksToUpdate });

      lastRolloverDateRef.current = today;

      await new Promise(resolve => setTimeout(resolve, 500));

      setTotalMinutesAllEntries(0);

      await fetchActiveEntry();

    } catch (error) {
      console.error('Error during date rollover:', error);
    } finally {
      setHasRolledOverDate(false);
      rolloverInProgressRef.current = false;
    }
  }, [addTimeManagement, fetchActiveEntry]);

  useEffect(() => {
    if (isLoading || isPaused) {
      return;
    }

    const updateElapsedTime = async () => {
      const entries = allEntriesRef.current;
      if (!entries || entries.length === 0) return;

      let totalMinutesForAllEntries = 0;
      const currentEntryId = timeManagementIdRef.current;

      const updatedEntries = entries.map((entry: any) => {
        const isCurrentEntry = entry.id === currentEntryId;
        let entryTotal = 0;

        if (entry.time_sessions && entry.time_sessions.length > 0) {
          entry.time_sessions.forEach((session: any) => {
            if (session.start_time) {
              const startParts = session.start_time.split(':');
              const startHours = parseInt(startParts[0]) || 0;
              const startMins = parseInt(startParts[1]) || 0;
              const startSecs = parseInt(startParts[2]) || 0;
              const sessionStartMinutes = startHours * 60 + startMins + (startSecs / 60);

              let sessionEndMinutes;

              if (session.end_time) {
                const endParts = session.end_time.split(':');
                const endHours = parseInt(endParts[0]) || 0;
                const endMins = parseInt(endParts[1]) || 0;
                const endSecs = parseInt(endParts[2]) || 0;
                sessionEndMinutes = endHours * 60 + endMins + (endSecs / 60);

                const sessionDuration = sessionEndMinutes - sessionStartMinutes;
                if (sessionDuration > 0) {
                  entryTotal += sessionDuration;
                }
              } else if (session.is_active && isCurrentEntry) {
                const now = new Date();
                sessionEndMinutes = now.getHours() * 60 + now.getMinutes() + (now.getSeconds() / 60);

                const sessionDuration = sessionEndMinutes - sessionStartMinutes;
                if (sessionDuration > 0) {
                  entryTotal += sessionDuration;
                }
              }
            }
          });
        }

        totalMinutesForAllEntries += entryTotal;

        return {
          ...entry,
          total_minutes: Math.round(entryTotal)
        };
      });

      setTotalMinutesAllEntries(Math.round(totalMinutesForAllEntries));
      setAllEntries(updatedEntries);
      allEntriesRef.current = updatedEntries;

      if (totalMinutesForAllEntries >= 240 && totalMinutesForAllEntries < 245 && !hasShownLunchPrompt() && !showLunchDialog) {
        setShowLunchDialog(true);
        const key = getLunchPromptKey();
        sessionStorage.setItem(key, 'true');
      }

      const today = new Date().toISOString().split('T')[0];
      if (
        totalMinutesForAllEntries >= 1440 &&
        !hasRolledOverDate &&
        !rolloverInProgressRef.current &&
        lastRolloverDateRef.current !== today
      ) {
        setHasRolledOverDate(true);

        try {
          const currentEntry = entries.find((e: any) => e.id === currentEntryId);
          if (currentEntry) {
            await rolloverToNewDate(currentEntry);
          }
        } catch (error) {
          console.error('Error rolling over to new date:', error);
          setHasRolledOverDate(false);
          rolloverInProgressRef.current = false;
        }
      }

      const standardDayMinutes = apiStandardHours ?? resolveStandardDayMinutes(null, standardWorkingHoursPerDay);
      
      if (
        totalMinutesForAllEntries >= standardDayMinutes &&
        !hasShownOvertimeDialogRef.current &&
        !isOvertimeMode
      ) {
        setShowOvertimeDialog(true);
        hasShownOvertimeDialogRef.current = true;
        setOvertimePromptShown();
      }
    };

    updateElapsedTime();
    const timer = setInterval(updateElapsedTime, 10000);
    return () => clearInterval(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    isPaused,
    showLunchDialog,
    hasRolledOverDate,
    rolloverToNewDate,
    isOvertimeMode,
    apiStandardHours,
    standardWorkingHoursPerDay
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isPaused && !isLoading) {
        const entries = allEntriesRef.current;
        if (!entries || entries.length === 0) return;

        let totalMinutesForAllEntries = 0;
        const currentEntryId = timeManagementIdRef.current;

        entries.forEach((entry: any) => {
          const isCurrentEntry = entry.id === currentEntryId;

          if (entry.time_sessions && entry.time_sessions.length > 0) {
            let entryTotal = 0;

            entry.time_sessions.forEach((session: any) => {
              if (session.start_time) {
                const startParts = session.start_time.split(':');
                const startHours = parseInt(startParts[0]) || 0;
                const startMins = parseInt(startParts[1]) || 0;
                const startSecs = parseInt(startParts[2]) || 0;
                const sessionStartMinutes = startHours * 60 + startMins + (startSecs / 60);

                let sessionEndMinutes;

                if (session.end_time) {
                  const endParts = session.end_time.split(':');
                  const endHours = parseInt(endParts[0]) || 0;
                  const endMins = parseInt(endParts[1]) || 0;
                  const endSecs = parseInt(endParts[2]) || 0;
                  sessionEndMinutes = endHours * 60 + endMins + (endSecs / 60);

                  const sessionDuration = sessionEndMinutes - sessionStartMinutes;
                  if (sessionDuration > 0) {
                    entryTotal += sessionDuration;
                  }
                } else if (session.is_active && isCurrentEntry) {
                  const now = new Date();
                  sessionEndMinutes = now.getHours() * 60 + now.getMinutes() + (now.getSeconds() / 60);

                  const sessionDuration = sessionEndMinutes - sessionStartMinutes;
                  if (sessionDuration > 0) {
                    entryTotal += sessionDuration;
                  }
                }
              }
            });

            totalMinutesForAllEntries += entryTotal;
          }
        });

        setTotalMinutesAllEntries(Math.round(totalMinutesForAllEntries));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPaused, isLoading]);

  const formatTime = (totalMinutes: number) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return {
      hours: hours.toString().padStart(2, '0'),
      minutes: minutes.toString().padStart(2, '0'),
      formatted: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    };
  };
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (locationState?.fromList &&
      locationState?.newEntryData &&
      !hasProcessedNavigationRef.current) {

      hasProcessedNavigationRef.current = true;
      const newEntryData = locationState.newEntryData;

      const handleNewEntry = async () => {
        try {
          const projectId = Number(newEntryData.projectId);
          const machineId = newEntryData.machineId ? Number(newEntryData.machineId) : null;
          const activityId = newEntryData.activityId ? Number(newEntryData.activityId) : null;
          const modeType = Number(newEntryData.modeType);
          const notes = newEntryData.notes || undefined;

          const response = await getActiveEntry();

          if (!isMountedRef.current) return;
          if (!response?.data) {
            if (isMountedRef.current) {
              history.replace(location.pathname, {});
            }
            return;
          }

          const freshEntries = Array.isArray(response.data)
            ? response.data
            : (response.data.entries || []);

          let existingEntry: any = null;

          for (const entry of freshEntries) {
            if (Number(entry.project?.id) !== projectId) continue;
            if (Number(entry.mode_type) !== modeType) continue;

            const entryMachineId = entry.machine?.id ? Number(entry.machine.id) : null;
            const entryActivityId = entry.activity?.id ? Number(entry.activity.id) : null;

            let isMatch = false;

            if (modeType === 0) {
              isMatch = (entryMachineId === machineId && entryActivityId === activityId);
            } else if (modeType === 1) {
              isMatch = (entryActivityId === activityId && entryMachineId === null);
            }  else if (modeType === 2) {
                isMatch = doEntriesMatch(entry, projectId, null, null, 2, notes);
            }
            if (isMatch) {
              existingEntry = entry;
              break;
            }
          }

          if (!isMountedRef.current) return;

          if (existingEntry) {
            const isAlreadyActive = existingEntry.status === 2 && existingEntry.is_active === true;

            if (!isAlreadyActive) {
              await pauseResumeTimer({
                id: existingEntry.id,
                action: 'resume'
              });
              if (!isMountedRef.current) return;
              await new Promise(resolve => setTimeout(resolve, 300));
            }

            if (!isMountedRef.current) return;

            await fetchActiveEntry(true);

          } else {
            const dataToSave: any = {
              project_id: projectId,
              mode_type: modeType,
              status: 2,
              auto_pause_others: true,
              date: new Date().toISOString().split('T')[0]
            };

            if (machineId) dataToSave.machine_id = machineId;
            if (activityId) dataToSave.activity_id = activityId;
            if (notes) dataToSave.notes = notes; 

            await addTimeManagement(dataToSave);
            if (!isMountedRef.current) return;
            await new Promise(resolve => setTimeout(resolve, 300));
            if (!isMountedRef.current) return;

            await fetchActiveEntry(true);
          }

        } catch (error: any) {
          console.error('❌ Error:', error);
          if (isMountedRef.current) {
            await fetchActiveEntry(true);
          }
        } finally {
          if (isMountedRef.current) {
            history.replace(location.pathname, {});
          }
        }
      };

      handleNewEntry();
    }

    if (!locationState?.fromList) {
      hasProcessedNavigationRef.current = false;
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState?.fromList, locationState?.newEntryData]);

  const isInitialMountRef = useRef(true);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
    }
  }, []);

  const doEntriesMatch = (
    entry: any,
    projectId: number,
    machineId: number | null,
    activityId: number | null,
    modeType: number,
    notes?: string 
  ) => {
    const entryProjectId = entry.project?.id;
    if (!entryProjectId || entryProjectId !== projectId) {
      return false;
    }

    if (entry.mode_type !== modeType) {
      return false;
    }

    const entryMachineId = entry.machine?.id || null;
    const entryActivityId = entry.activity?.id || null;

    if (modeType === 0) {
      return entryMachineId === machineId && entryActivityId === activityId;
    }
    if (modeType === 1) {
      return entryActivityId === activityId && entryMachineId === null;
    }
    if (modeType === 2) {
      const entryNotes = (entry.notes || '').trim();
      const incomingNotes = (notes || '').trim();
      return entryMachineId === null && entryActivityId === null && entryNotes === incomingNotes;
    }

    return false;
  };

  const fetchActivities = async () => {
    setLoadingActivities(true);
    try {
      const response = await getActivities();
      setActivities(response.data || []);
    } catch (error) {
      console.log('Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleProjectClick = () => {
    history.push(route(TIME_MANAGEMENT_ROUTES.list), {
      fromActiveTime: true,
      returnToActiveTime: true
    });
  };
  const handleMachineClick = () => {
    const currentProject = entryData?.project;
    history.push(route(TIME_MANAGEMENT_ROUTES.list), {
      fromActiveTime: true,
      returnToActiveTime: true,
      focusOnMachine: true,
      preSelectedProjectId: currentProject?.id,
      preSelectedProject: currentProject,
      preSelectedMode: 'maskin'
    });
  };
  const closeActivityModal = () => {
    setShowActivityModal(false);
    setSelectedActivity(null);
  };

  const handleActivitySelect = (activity: any) => {
    setSelectedActivity(activity);
  };

  const handleConfirmActivitySelection = async () => {
    if (!selectedActivity) {
      return;
    }

    try {
      closeActivityModal();
      setIsLoading(true);

      const projectId = entryData?.project?.id;
      const activityId = selectedActivity.id;
      const existingEntry = allEntries.find((entry: any) =>
        doEntriesMatch(entry, projectId, null, activityId, 1)
      );

      if (existingEntry) {
        const isAlreadyActive = existingEntry.status === 2 && existingEntry.is_active === true;

        if (!isAlreadyActive) {
          await pauseResumeTimer({
            id: existingEntry.id,
            action: 'resume'
          });
        }

        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchActiveEntry();

      } else {
        const newEntryData: any = {
          mode_type: 1,
          activity_id: activityId,
          date: new Date().toISOString().split('T')[0],
          status: 2,
          auto_pause_others: true
        };

        if (projectId) {
          newEntryData.project_id = projectId;
        }

        await addTimeManagement(newEntryData);
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchActiveEntry();
      }
    } catch (error: any) {
      console.error('Activity selection error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnspecifiedClick = () => {
    setUnspecifiedDescription('');
    setShowUnspecifiedModal(true);
  };

  const handleConfirmUnspecified = async () => {
    setShowUnspecifiedModal(false);
    try {
      setIsLoading(true);

      const projectId = entryData?.project?.id;
      const existingEntry = allEntries.find((entry: any) =>
        doEntriesMatch(entry, projectId, null, null, 2, unspecifiedDescription) 
      );

      if (existingEntry) {
        const isAlreadyActive = existingEntry.status === 2 && existingEntry.is_active === true;

        if (!isAlreadyActive) {
          await pauseResumeTimer({
            id: existingEntry.id,
            action: 'resume'
          });
        }

        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchActiveEntry();

      } else {
        const newEntryData: any = {
          mode_type: 2,
          date: new Date().toISOString().split('T')[0],
          status: 2,
          auto_pause_others: true,
          notes: unspecifiedDescription 
        };

        if (projectId) {
          newEntryData.project_id = projectId;
        }

        await addTimeManagement(newEntryData);
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchActiveEntry();
      }
    } catch (error: any) {
      console.error('Unspecified click error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const mainTime = formatTime(totalMinutesAllEntries);
  const standardDayMinutes = apiStandardHours ?? resolveStandardDayMinutes(null, standardWorkingHoursPerDay);
  const progressPercentage = standardDayMinutes > 0
    ? Math.min((totalMinutesAllEntries / standardDayMinutes) * 100, 100)
    : 0;
  const radius = 130;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (circumference * progressPercentage) / 100;

  const overtimeMinutes = totalMinutesAllEntries > standardDayMinutes
    ? totalMinutesAllEntries - standardDayMinutes
    : 0;
  const overtimeTime = formatTime(overtimeMinutes);

  const handlePauseToggle = async () => {
    const timeManagementId = timeManagementIdRef.current;
    if (!timeManagementId) {
      return;
    }
    setIsPauseResuming(true);
    try {
      const action = isPaused ? 'resume' : 'pause';
      const result = await pauseResumeTimer({
        id: timeManagementId,
        action: action
      });

      if (result?.success || result?.status === 'success') {
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchActiveEntry();
      } else {
        throw new Error('Timer update failed');
      }

    } catch (error: any) {
      console.log('Error details:', {
        message: error?.message,
        response: error?.response,
        data: error?.response?.data
      });

      await fetchActiveEntry();
    } finally {
      setIsPauseResuming(false);
    }
  };

  const handleResume = async () => {
    const timeManagementId = timeManagementIdRef.current;

    if (!timeManagementId) {
      return;
    }

    setIsPauseResuming(true);

    try {
      await pauseResumeTimer({
        id: timeManagementId,
        action: 'resume'
      });

      setShowResumeUI(false);
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchActiveEntry();

    } catch (error: any) {
      const errorMsg = error?.message || String(error);
      if (errorMsg.includes('resumed') || errorMsg.includes('success')) {
        setShowResumeUI(false);
        await fetchActiveEntry();
      } else {
        console.log(`Failed to resume timer: ${errorMsg}`);
      }
    } finally {
      setIsPauseResuming(false);
    }
  };

  const handleEndDay = async () => {
    const timeManagementId = timeManagementIdRef.current;

    if (!timeManagementId) {
      return;
    }

    setIsStopping(true);

    try {
      if (!isPaused) {
        try {
          await pauseResumeTimer({
            id: timeManagementId,
            action: 'pause'
          });

          await new Promise(resolve => setTimeout(resolve, 300));

        } catch (pauseError: any) {
          console.error('Pause error before ending day:', pauseError);

          const errorMsg = String(pauseError);
          if (!errorMsg.includes('paused') && !errorMsg.includes('success')) {
            throw pauseError;
          }
        }
      }

      history.push({
        pathname: route(TIME_MANAGEMENT_ROUTES.dailySummary),
        state: {
          fromActiveTime: true,
          timeManagementId: timeManagementId
        }
      });

    } catch (error: any) {
      console.error('End day error:', error);
      setIsStopping(false);
    }
  };

  const handleActivityClick = async () => {
    setShowActivityModal(true);
    await fetchActivities();
  };

  const handleContinueWorking = () => {
    setShowLunchDialog(false);
  };

  const handleContinueAsOvertime = () => {
    setIsOvertimeMode(true);
    setShowOvertimeDialog(false);
    hasShownOvertimeDialogRef.current = true;
    setOvertimePromptShown();
  };

  const handleRegisterDay = async () => {
    setShowOvertimeDialog(false);
    hasShownOvertimeDialogRef.current = true;
    setOvertimePromptShown();

    const timeManagementId = timeManagementIdRef.current;

    if (!timeManagementId) {
      return;
    }

    setIsStopping(true);

    try {
      if (!isPaused) {
        await pauseResumeTimer({
          id: timeManagementId,
          action: 'pause'
        });

        setIsPaused(true);
      }

      history.push({
        pathname: route(TIME_MANAGEMENT_ROUTES.dailySummary),
        state: {
          fromActiveTime: true,
          timeManagementId: timeManagementId
        }
      });

    } catch (error) {
      setIsStopping(false);
    }
  };

  const handleTakeLunch = async () => {
    setShowLunchDialog(false);
    const timeManagementId = timeManagementIdRef.current;

    if (!timeManagementId) {
      return;
    }

    setIsPauseResuming(true);

    try {
      await pauseResumeTimer({
        id: timeManagementId,
        action: 'pause'
      });

      await new Promise(resolve => setTimeout(resolve, 500));

      setIsPaused(true);

      await fetchActiveEntry();

    } catch (error: any) {
      console.log('Pause error:', error);

      const errorMessage = error?.message || error?.toString() || '';
      const isActuallySuccess =
        errorMessage.includes('paused') ||
        errorMessage.includes('Timer paused') ||
        errorMessage.includes('successfully') ||
        errorMessage.includes('already paused');

      if (isActuallySuccess) {
        setIsPaused(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchActiveEntry();
      } else {
        console.log(t('timeManagement.couldNotPauseLunch'));
      }
    } finally {
      setIsPauseResuming(false);
    }
  };

  if (isLoading) {
    return (
      <div className={classes.activeTimeWrapper}>
        <div className={classes.content}>
          <div style={{ textAlign: 'center', padding: '40px' }}>{t('timeManagement.loading')}</div>
        </div>
      </div>
    );
  }

  const showAnyOption = entryData?.mode_type !== 3;

  return (
    <div className={classes.activeTimeWrapper}>
      {showActivityModal && (
        <div className={classes.modalOverlay} onClick={closeActivityModal}>
          <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>
              <h3 className={classes.modalTitle}>{t('timeManagement.selectTask')}</h3>
              <button className={classes.closeButton} onClick={closeActivityModal}>
                ×
              </button>
            </div>

            <div className={classes.activityList}>
              {loadingActivities ? (
                <div className={classes.loadingMessage}>{t('timeManagement.loading')}</div>
              ) : activities.length === 0 ? (
                <div className={classes.emptyMessage}>{t('timeManagement.noTasksFound')}</div>
              ) : (
                <div className={classes.activityPills}>
                  {activities.map((activity) => (
                    <button
                      key={activity.id}
                      className={`${classes.activityPill} ${selectedActivity?.id === activity.id ? classes.activityPillActive : ''}`}
                      onClick={() => handleActivitySelect(activity)}
                    >
                      {activity.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={classes.modalFooter}>
              <button
                className={classes.selectButton}
                onClick={handleConfirmActivitySelection}
                disabled={!selectedActivity}
              >
                {t('timeManagement.select')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUnspecifiedModal && (
        <div className={classes.modalOverlay} onClick={() => setShowUnspecifiedModal(false)}>
          <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>
              <h3 className={classes.modalTitle}>{t('timeManagement.unspecified')}</h3>
              <button className={classes.closeButton} onClick={() => setShowUnspecifiedModal(false)}>×</button>
            </div>
            <div style={{ padding: '16px' }}>
              <textarea
                value={unspecifiedDescription}
                onChange={(e) => setUnspecifiedDescription(e.target.value)}
                placeholder={t('timeManagement.descriptionPlaceholder') || 'Description...'}
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div className={classes.modalFooter}>
              <button className={classes.selectButton} onClick={handleConfirmUnspecified}>
                {t('timeManagement.select')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showOvertimeDialog && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            pointerEvents: 'all'
          }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                console.log('⏰ Clicked backdrop - dialog stays open');
              }
            }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '460px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              position: 'relative',
              zIndex: 1000000
            }}
              onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontSize: '64px',
                textAlign: 'center',
                marginBottom: '16px'
              }}>
                ⏰
              </div>
              <h3 style={{
                margin: '0 0 16px 0',
                fontSize: '28px',
                fontWeight: '700',
                color: '#333',
                textAlign: 'center'
              }}>
                {t('timeManagement.eightHoursRegistered')}
              </h3>
              <p style={{
                margin: '0 0 32px 0',
                fontSize: '18px',
                color: '#666',
                lineHeight: '1.6',
                textAlign: 'center'
              }}>
                {t('timeManagement.eightHoursMessage')}
              </p>
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={handleContinueAsOvertime}
                  style={{
                    padding: '14px 28px',
                    fontSize: '18px',
                    fontWeight: '600',
                    border: '2px solid #4CAF50',
                    backgroundColor: 'white',
                    color: '#4CAF50',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '140px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f8f4';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {t('timeManagement.continue')}
                </button>
                <button
                  onClick={handleRegisterDay}
                  disabled={isStopping}
                  style={{
                    padding: '14px 28px',
                    fontSize: '18px',
                    fontWeight: '600',
                    border: 'none',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: isStopping ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '140px',
                    opacity: isStopping ? 0.6 : 1
                  }}
                  onMouseOver={(e) => {
                    if (!isStopping) {
                      e.currentTarget.style.backgroundColor = '#1976D2';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#2196F3';
                  }}
                >
                  {isStopping ? t('timeManagement.registering') : t('timeManagement.registerTheDay')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {showLunchDialog && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            pointerEvents: 'all'
          }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                console.log('🍽️ Clicked backdrop - dialog stays open');
              }
            }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              position: 'relative',
              zIndex: 1000000
            }}
              onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontSize: '64px',
                textAlign: 'center',
                marginBottom: '16px'
              }}>
                🍽️
              </div>
              <h3 style={{
                margin: '0 0 16px 0',
                fontSize: '28px',
                fontWeight: '700',
                color: '#333',
                textAlign: 'center'
              }}>
                {t('timeManagement.takeLunch')}
              </h3>
              <p style={{
                margin: '0 0 32px 0',
                fontSize: '18px',
                color: '#666',
                lineHeight: '1.6',
                textAlign: 'center'
              }}>
                {t('timeManagement.fourHoursWorkedMessage')}
              </p>
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={handleContinueWorking}
                  style={{
                    padding: '14px 28px',
                    fontSize: '18px',
                    fontWeight: '600',
                    border: '2px solid #4CAF50',
                    backgroundColor: 'white',
                    color: '#4CAF50',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '140px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f8f4';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {t('timeManagement.continue')}
                </button>
                <button
                  onClick={handleTakeLunch}
                  disabled={isPauseResuming}
                  style={{
                    padding: '14px 28px',
                    fontSize: '18px',
                    fontWeight: '600',
                    border: 'none',
                    backgroundColor: '#FF6B35',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: isPauseResuming ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '140px',
                    opacity: isPauseResuming ? 0.6 : 1
                  }}
                  onMouseOver={(e) => {
                    if (!isPauseResuming) {
                      e.currentTarget.style.backgroundColor = '#E55A28';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#FF6B35';
                  }}
                >
                  {isPauseResuming ? t('timeManagement.pausing') : t('timeManagement.takeLunchButton')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className={classes.content}>
        <div className={classes.timerContainer}>
          <svg className={classes.progressRing} width="280" height="280">
            <circle className={classes.progressRingCircleBg} cx="140" cy="140" r={radius} />
            <circle
              className={classes.progressRingCircle}
              cx="140"
              cy="140"
              r={radius}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{ opacity: isPaused ? 0.5 : 1, transition: 'opacity 0.3s ease' }}
            />
          </svg>

          <div className={classes.timeDisplay}>
            <div className={classes.time}>
              {mainTime.formatted}
            </div>
            <div className={classes.timeLabel}>
              {isPaused ? t('timeManagement.pause') : isOvertimeMode ? t('timeManagement.activeTime') : t('timeManagement.activeTime')}
            </div>
          </div>
        </div>
        {showResumeUI ? (
          <div className={classes.resumeContainer}>
            <div className={classes.pausedLabel}>{t('timeManagement.timePaused')}</div>
            <button
              onClick={handleResume}
              className={classes.resumeButton}
              disabled={isPauseResuming}
            >
              {isPauseResuming ? t('timeManagement.resuming') : t('timeManagement.continue')}
            </button>
          </div>
        ) : (
          <div className={classes.buttonContainer}>
            <button
              onClick={handlePauseToggle}
              className={classes.pauseButton}
              disabled={isStopping || isPauseResuming}
            >
              <span className={classes.pauseIcon}>{isPaused ? '▶' : '❚❚'}</span>
              {isPauseResuming ? (isPaused ? t('timeManagement.resuming') : t('timeManagement.pausing')) : (isPaused ? t('timeManagement.continue') : t('timeManagement.pause'))}
            </button>

            <button
              onClick={handleEndDay}
              disabled={isStopping || isPauseResuming}
              className={classes.endButton}
            >
              {isStopping ? t('timeManagement.ending') : t('timeManagement.endTheDay')}
            </button>
          </div>
        )}



        {showAnyOption && !showResumeUI && (
          <div className={classes.switchActivitySection}>
            <div className={classes.sectionTitle}>{t('timeManagement.switchActivity')}</div>

            <div className={classes.activityCardsGrid}>
              <button className={classes.activityCard} onClick={handleProjectClick}>
                <div className={classes.cardIconWrapper}>
                  <div className={classes.cardIcon}>📁</div>
                </div>
                <div className={classes.cardLabel}>{t('timeManagement.project') || 'Prosjekt'}</div>
              </button>

              <button className={classes.activityCard} onClick={handleMachineClick}>
                <div className={classes.cardIconWrapper}>
                  <div className={classes.cardIcon}>🔧</div>
                </div>
                <div className={classes.cardLabel}>{t('timeManagement.machine') || 'Maskin'}</div>
              </button>
              <button className={classes.activityCard} onClick={handleActivityClick}>
                <div className={classes.cardIconWrapper}>
                  <div className={classes.cardIcon}>📄</div>
                </div>
                <div className={classes.cardLabel}>{t('timeManagement.task') || 'Oppgave'}</div>
              </button>
              <button className={classes.activityCard} onClick={handleUnspecifiedClick}>
                <div className={classes.cardIconWrapper}>
                  <div className={classes.cardIcon}>📊</div>
                </div>
                <div className={classes.cardLabel}>{t('timeManagement.unspecified') || 'Uspesifisert'}</div>
              </button>
            </div>
          </div>
        )}


        {allEntries.length > 0 && (
          <div className={classes.tasksSection}>
            <div className={classes.sectionTitle}>{t('timeManagement.allTasksToday', { count: allEntries.length })}</div>

            {allEntries.map((entry: any) => {
              const entryTime = formatTime(entry.total_minutes || 0);
              const isCurrentEntry = entry.id === timeManagementIdRef.current;
              const isEntryActive = entry.is_active === true;
              const isEntryPaused = !isEntryActive;

              return (
                <div key={entry.id} className={`${classes.taskCard} ${isCurrentEntry ? classes.activeEntry : ''}`}>
                  {(entry.mode_type === 0 || entry.mode_type === 1 || entry.mode_type === 2) && entry.project ? (
                    <div className={classes.taskItem}>
                      <div className={classes.taskIcon}>📁</div>
                      <div className={classes.taskDetails}>
                        <div className={classes.taskNumber}>
                          {entry.project.number}
                          {isCurrentEntry && <span className={classes.currentBadge}> ({t('timeManagement.current')})</span>}
                        </div>
                        {entry.machine && entry.mode_type === 0 && (
                          <div className={classes.taskMeta}>
                            {t('timeManagement.machine')}: {entry.machine.intern}
                          </div>
                        )}
                        {entry.activity && (entry.mode_type === 0 || entry.mode_type === 1) && (
                          <div className={classes.taskMeta}>
                            {t('timeManagement.activity')}: {entry.activity.name}
                          </div>
                        )}
                        {entry.notes && entry.mode_type === 2 && (
                          <div className={classes.taskMeta}>
                            {t('timeManagement.notes')}: {entry.notes}
                          </div>
                        )}
                        <div className={classes.taskMeta}>
                          {t('timeManagement.start')}: {entry.start_time?.substring(0, 5)}
                        </div>
                        {isEntryActive && (
                          <div className={classes.taskStatus}>
                            <span className={classes.activeIndicator}>●</span> {t('timeManagement.active')}
                          </div>
                        )}
                        {isEntryPaused && (
                          <div className={classes.taskStatus}>
                            <span className={classes.pausedIndicator}>⏸</span> {t('timeManagement.paused')}
                          </div>
                        )}
                      </div>
                      <div className={classes.taskTime}>
                       {entryTime.formatted}
                      </div>
                    </div>
                  ) : entry.mode_type === 3 ? (
                    <div className={classes.taskItem}>
                      <div className={classes.taskIcon}>📊</div>
                      <div className={classes.taskDetails}>
                        <div className={classes.taskNumber}>
                          {t('timeManagement.unspecified')}
                          {isCurrentEntry && <span className={classes.currentBadge}> ({t('timeManagement.current')})</span>}
                        </div>
                        <div className={classes.taskMeta}>
                          {t('timeManagement.start')}: {entry.start_time?.substring(0, 5)}
                        </div>
                        {isEntryActive && (
                          <div className={classes.taskStatus}>
                            <span className={classes.activeIndicator}>●</span> {t('timeManagement.active')}
                          </div>
                        )}
                        {isEntryPaused && (
                          <div className={classes.taskStatus}>
                            <span className={classes.pausedIndicator}>⏸</span> {t('timeManagement.paused')}
                          </div>
                        )}
                      </div>
                      <div className={classes.taskTime}>
                        {entryTime.formatted}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}

            {overtimeMinutes > 0 && (
              <div style={{
                marginTop: '12px',
                padding: '12px 16px',
                background: '#FFF3E0',
                borderRadius: '8px',
                border: '1px solid #FFB74D',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#E65100'
                }}>
                  {t('timeManagement.overtime')}:
                </span>
                <span style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#E65100',
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  {overtimeTime.formatted}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveTime;